import { NumberFormatter } from './NumberFormatter';

export interface ShotUnitType {
  decimals?: number | null;
  div_as_decimals?: boolean | null;
  div1: number;
  div2?: number | null;
  div3?: number | null;
  gravimetric?: boolean | null;
  mainunit: number;
  sym1?: string | null;
  sym2?: string | null;
  sym3?: string | null;
  unitname: string;
  display_name?: string;
}

type Divisor = [number, string];
type Divisors = Divisor[];

export class ShotFormatter {
  unit: ShotUnitType;
  divisors: Divisors;
  formatters: NumberFormatter[];
  ratios: number[];
  majorUnit: number;
  decimals: number;
  decimalDivisor: number;
  internalUnit: number;
  zeroAsString: string;

  constructor(
    unit: ShotUnitType | null | undefined = undefined,
    decimalSeparator: string | undefined = undefined,
    allowTrailingZeros = false
  ) {
    /**
     *      majorUnit (float) is typically a fluid ounce, in ml
     *      divisors is a sequence of sequences. Each sub-sequence must
     *           consist of an int that is the divisor of the fluid ounce
     *           and a string that appears after the fraction in string format
     *      decimals (int) is the number of decimal places for the last fraction
     *
     */
    if (unit) {
      this.unit = unit;
    } else {
      this.unit = {
        decimals: 0,
        div1: 1,
        div2: null,
        div3: null,
        gravimetric: false,
        mainunit: 1,
        sym1: null,
        sym2: null,
        sym3: null,
        unitname: 'ml',
      };
    }

    // Ensure there is at least one divisor and each divisor divides the next
    this.divisors = validDivisors(this.unit);

    // calculate ratio between successive divisors,
    this.ratios = this.divisors
      .slice(1)
      .map(([divisor], i) => divisor / this.divisors[i][0])
      .reverse(); // VolToList needs ratios in reverse order

    let decimalDivisor = 10 ** (this.unit.decimals ?? 0);
    if (this.unit.div_as_decimals && this.ratios.length > 0) {
      // Display the smallest division as a decimal of the preceding part.
      decimalDivisor = this.ratios.shift()!;
      this.divisors.pop();
    }

    // and create formatter for each divisor. Last one gets the specified decimals
    this.formatters = this.divisors.map(([_, symbol], i) => {
      const last = i === this.divisors.length - 1;
      return new NumberFormatter({
        decimals: last ? this.unit.decimals : 0,
        decimalSeparator: last ? decimalSeparator : undefined,
        allowTrailingZeros,
        groupSeparator: '',
        symbol,
      });
    });

    this.majorUnit = this.unit.mainunit || 1.0;
    this.decimals = this.unit.decimals != null ? this.unit.decimals : 0;
    this.decimalDivisor = decimalDivisor;
    this.internalUnit =
      this.majorUnit /
      this.divisors[this.divisors.length - 1][0] /
      this.decimalDivisor;
    this.zeroAsString = this.getString(0);
  }
  getUnit(): ShotUnitType {
    return this.unit;
  }
  getDecimals(): number {
    return this.decimals;
  }
  getUnitName(): string {
    return this.unit.display_name || this.unit.unitname;
  }
  getShotInMl(specificgravity: number): number {
    // In decimal mode this is the smallest displayed part, not the smallest
    // physical division. The physical division is this value / decimalDivisor.
    const shot = this.majorUnit / this.divisors[this.divisors.length - 1][0];
    return this.unit.gravimetric ? shot / specificgravity : shot;
  }
  isGravimetric(): boolean {
    return !!this.unit.gravimetric;
  }
  format(
    {
      volume,
      specificgravity,
    }: { volume?: number | null; specificgravity: number },
    withPlusSign = false
  ): string {
    if (volume == null) {
      return '';
    }
    return this.getString(
      this.unit.gravimetric ? volume * specificgravity : volume,
      withPlusSign
    );
  }
  getString(vol: number, withPlusSign = false): string {
    const res = this.VolToList(vol)
      .map((value, i) => this.formatters[i].format(value))
      .join(' ');

    if (withPlusSign && vol > 0 && res !== this.zeroAsString) {
      return '+' + res;
    }

    return res;
  }
  VolToList(volume: number): number[] {
    let u = Math.round(volume / this.internalUnit) / this.decimalDivisor;
    const res = [];
    for (const ratio of this.ratios) {
      res.push(u % ratio);
      u = Math.trunc(u / ratio);
    }
    res.push(u);
    res.reverse();

    return res;
  }
  ListToVol(lst: (number | string)[]): number {
    /* the inverse of VolToList */
    let rs = 0;
    for (let i = 0; i < Math.min(this.divisors.length, lst.length); i++) {
      rs +=
        (this.majorUnit / this.divisors[i][0]) *
        this.formatters[i].parse(lst[i]);
    }
    return rs;
  }
  unformat(str: string, specificgravity: number): number {
    const lst = str.trim().split(' ');
    return this.unit.gravimetric
      ? this.ListToVol(lst) / specificgravity
      : this.ListToVol(lst);
  }
}

/**
 * Returns the valid divisors for a given ShotUnitType.
 * A divisor is considered valid if it is non-zero and each subsequent divisor is divisible by the previous one.
 * If no valid divisors are found, a default divisor of [[1, '']] is returned.
 */
const validDivisors = (unit: ShotUnitType): Divisors => {
  const divisors = [
    [unit.div1, unit.sym1],
    [unit.div2, unit.sym2],
    [unit.div3, unit.sym3],
  ].filter((x) => x[0]) as Divisors;
  if (divisors.length) {
    const result = [divisors[0]];
    for (let i = 1; i < divisors.length; i++) {
      if (divisors[i][0] % divisors[i - 1][0] === 0) {
        result.push(divisors[i]);
      } else {
        break;
      }
    }
    return result;
  }
  return [[1, '']];
};
